import { useEffect, useId, useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Download, Send } from 'lucide-react'
import { ApiError, exportForecastCsv, publishForecast } from '@/api/client'
import type { ForecastView } from '@/api/types'
import { useAppChrome } from '@/context/AppChromeContext'
import { IconButton } from '@/components/IconButton'
import { useToast } from '@/components/toast-context'

interface Props {
  views: ForecastView[]
  onPublished?: () => void
}

export function ExportPublishToolbar({ views, onPublished }: Props) {
  const { year, viewCode } = useAppChrome()
  const toast = useToast()
  const [exportOpen, setExportOpen] = useState(false)
  const [publishOpen, setPublishOpen] = useState(false)
  const [includeLost, setIncludeLost] = useState(false)
  const [exportView, setExportView] = useState(viewCode)
  const [baselineName, setBaselineName] = useState('')
  const [commentary, setCommentary] = useState('')
  const exportRef = useRef<HTMLFormElement>(null)
  const publishRef = useRef<HTMLFormElement>(null)
  const exportTitleId = useId()
  const publishTitleId = useId()

  useEffect(() => {
    if (!exportOpen && !publishOpen) return

    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setExportOpen(false)
        setPublishOpen(false)
      }
    }

    function onPointer(event: MouseEvent) {
      const target = event.target as Node
      if (exportOpen && exportRef.current && !exportRef.current.contains(target)) {
        setExportOpen(false)
      }
      if (publishOpen && publishRef.current && !publishRef.current.contains(target)) {
        setPublishOpen(false)
      }
    }

    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onPointer)
    const focusTarget = exportOpen ? exportRef.current : publishRef.current
    window.setTimeout(() => {
      focusTarget?.querySelector<HTMLElement>('select, textarea, button')?.focus()
    }, 0)

    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onPointer)
    }
  }, [exportOpen, publishOpen])

  const exportMutation = useMutation({
    mutationFn: () =>
      exportForecastCsv({
        year,
        view_code: exportView,
        include_lost: includeLost,
      }),
    onSuccess: () => {
      toast.success('Export started — check your downloads.')
      setExportOpen(false)
    },
    onError: (caught) => {
      toast.error(caught instanceof ApiError ? caught.message : 'Export failed.')
    },
  })

  const publishMutation = useMutation({
    mutationFn: () =>
      publishForecast({
        name: baselineName.trim(),
        forecast_year: year,
        commentary: commentary.trim() || null,
      }),
    onSuccess: (snapshot) => {
      toast.success(`Baseline “${snapshot.name}” published.`)
      setPublishOpen(false)
      setBaselineName('')
      setCommentary('')
      onPublished?.()
    },
    onError: (caught) => {
      toast.error(caught instanceof ApiError ? caught.message : 'Publish failed.')
    },
  })

  return (
    <div className="export-publish">
      <IconButton
        label="Export forecast"
        variant="ghost"
        onClick={() => {
          setPublishOpen(false)
          setExportOpen((v) => !v)
        }}
      >
        <Download size={18} strokeWidth={2} />
      </IconButton>
      <IconButton
        label="Publish forecast"
        variant="ghost"
        onClick={() => {
          setExportOpen(false)
          setPublishOpen((v) => !v)
        }}
      >
        <Send size={18} strokeWidth={2} />
      </IconButton>

      {exportOpen && (
        <form
          ref={exportRef}
          className="popover"
          role="dialog"
          aria-modal="true"
          aria-labelledby={exportTitleId}
          onSubmit={(event) => {
            event.preventDefault()
            exportMutation.mutate()
          }}
        >
          <h3 id={exportTitleId} className="popover__title">
            Export CSV
          </h3>
          <label className="field">
            <span className="field__label">View</span>
            <select
              className="field__input"
              value={exportView}
              onChange={(event) => setExportView(event.target.value)}
            >
              {views.map((view) => (
                <option key={view.id} value={view.code}>
                  {view.name}
                </option>
              ))}
            </select>
          </label>
          <label className="checkbox">
            <input
              type="checkbox"
              checked={includeLost}
              onChange={(event) => setIncludeLost(event.target.checked)}
            />
            Include lost
          </label>
          <div className="popover__actions">
            <button
              type="button"
              className="button button--ghost"
              onClick={() => setExportOpen(false)}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="button button--primary"
              disabled={exportMutation.isPending}
            >
              {exportMutation.isPending ? 'Exporting…' : 'Export'}
            </button>
          </div>
        </form>
      )}

      {publishOpen && (
        <form
          ref={publishRef}
          className="popover popover--wide"
          role="dialog"
          aria-modal="true"
          aria-labelledby={publishTitleId}
          onSubmit={(event) => {
            event.preventDefault()
            publishMutation.mutate()
          }}
        >
          <h3 id={publishTitleId} className="popover__title">
            Publish baseline ({year})
          </h3>
          <label className="field">
            <span className="field__label">Baseline name *</span>
            <input
              className="field__input"
              value={baselineName}
              required
              maxLength={200}
              onChange={(event) => setBaselineName(event.target.value)}
              placeholder="e.g. Q1 kickoff"
            />
          </label>
          <p className="field__hint">
            Publishes every non-lost opportunity for {year}, including probability, weighting, bill
            rate, base headcount, and monthly amounts.
          </p>
          <label className="field">
            <span className="field__label">Commentary</span>
            <textarea
              className="field__input field__input--textarea"
              rows={3}
              value={commentary}
              onChange={(event) => setCommentary(event.target.value)}
              placeholder="Headline notes for reviewers…"
            />
          </label>
          <div className="popover__actions">
            <button
              type="button"
              className="button button--ghost"
              onClick={() => setPublishOpen(false)}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="button button--primary"
              disabled={publishMutation.isPending || !baselineName.trim()}
            >
              {publishMutation.isPending ? 'Publishing…' : 'Confirm publish'}
            </button>
          </div>
        </form>
      )}
    </div>
  )
}
